import { LightningElement } from "lwc";
import { logger } from "@devforce/trailhead-client-logger";

import type { ToastOptions } from "./types";
export type { ToastOptions };

interface Toast extends ToastOptions {
  key: string;
  onClose: () => void;
}

const toastDefaults: ToastOptions = {
  duration: 6000,
  variant: "info",
  message: "",
  toasterName: "",
};

let toasts: Toast[] = [];
let uid = 0;
let elementCallback: undefined | (() => void);

const updateElementAfter = <T, U>(fn: (arg: T) => U) => {
  return (arg: T) => {
    let result = fn(arg);
    elementCallback && elementCallback();
    return result;
  };
};

const removeToast = updateElementAfter((key: string) => {
  toasts = toasts.filter((t) => t.key !== key);
});

export const showToast = updateElementAfter(
  (options: Partial<ToastOptions>) => {
    uid++;
    let key = `toast-${uid}`;
    let timer: number;
    const toast: Toast = {
      ...toastDefaults,
      ...options,
      key,
      onClose: () => {
        const customEvent = new CustomEvent("toasterclosed", {
          detail: { toasterName: toast.toasterName },
        });
        removeToast.call(null, key);
        document.dispatchEvent(customEvent);
        // Clear the previously registered timer
        if (timer) {
          clearTimeout(timer);
        }
      },
    };

    if (toast.duration) {
      timer = window.setTimeout(toast.onClose, toast.duration);
    }

    toasts = [...toasts, toast];
  }
);

export const reset = () => {
  updateElementAfter(() => {
    toasts = [];
  })(undefined);
};

export default class extends LightningElement {
  private toasts: Toast[] = [];

  /**
   * The toaster is a singleton where there is a single set of toasts where only
   * one component instance should be present on any given page. As such, the
   * component checks if there is already an element handling toasts to prevent
   * duplicate toasts from displaying.
   */
  connectedCallback() {
    if (!elementCallback) {
      elementCallback = this.update;
      this.update();
    } else {
      logger.warn(
        "An instance of tds-toaster is already present. Only the first instance will render toasts."
      );
    }
  }

  disconnectedCallback() {
    if (elementCallback === this.update) {
      elementCallback = undefined;
    }
  }

  private update = () => {
    this.toasts = toasts;
  };
}
